// Third party components
import React, { useEffect, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';

// MUI components
import {Card, CardContent, Container, Divider, Grid, Skeleton, Typography} from '@mui/material';

// Storybook components
import { Button, LoadingSpinner, Modal, PermissionFilter} from '@timmons-group/shared-react-components';
import { ACLS } from 'constants';

//PAM components
import CommunityOverview from 'PAM/components/CommunityOverview';
import CommunityPlans from 'PAM/components/CommunityPlans';

// Hooks
import { useCommunity, ACTIONS } from 'PAM/hooks/useCommunity';

const ViewCommunity = () => {
  let { id } = useParams();
  const bigStore = useCommunity();
  const { communityState, deleteCommunity, unmount, dispatch } = bigStore;
  const { community, modifying, loading } = communityState;
  const { plans } = communityState || {};
  const [deleteModal, setDeleteModal] = useState(false);

  // This effect will also fire if the id changes in the url
  useEffect(() => {
    if(id) {
      dispatch({type: ACTIONS.SET_ID, payload: id});
    }

    // Unmount on cleanup to reset the state
    return () => {
      unmount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const confirmDelete = () => {
    setDeleteModal(false);
    deleteCommunity();
  }

  const showDeleteModal = () => {
    setDeleteModal(true);
  }

  const closeDeleteModal = () => {
    setDeleteModal(false);
  }

  const deleteArea = () => {
    if (!community || loading) {
      return (<Skeleton />);
    }

    if (community) {
      const renderDeleteModal = () => {
        if (plans?.length > 0) {
          return (
            <Modal open={deleteModal} title="Cannot Delete Community" hideCancel={true} onCancel={closeDeleteModal} onOk={closeDeleteModal}>
              <Typography>
              A Community cannot be deleted if there are any Plans associated to it.
              </Typography>
              <br></br>
              <Typography>
              If the Community and associated Plans are invalid, please first delete the Plans before deleting the Community.
              </Typography>
            </Modal>
          );
        }

        return (
          <Modal open={deleteModal} title="Confirm Delete" onCancel={closeDeleteModal} onOk={confirmDelete} okLabel="Delete Community">
            <Typography>
                Are you sure you want to delete this Community from the Collaborative Planning Portal?
            </Typography>
            <br></br>
            <Typography>
                Please note it may take a couple of minutes for the Community map to be updated.
            </Typography>
          </Modal>
        );
      }

      return (
        <>
          <Divider />
          <Button onClick={showDeleteModal} label="Delete Community" variant="panel"/>
          {renderDeleteModal()}
        </>
      )
    }

    // Maybe render something to say you can't delete this community
    return null;
  }

  const renderPanel = () => {
    return (
      <>
        <Typography variant="panelHeader">{!community ? (<Skeleton height={50}/>) : community?.name}</Typography>
        <Divider />
        {/* This is the syntax to get back to this route's "home". "end" is needed to correctly match for active state */}
        {!community || loading ? (<Skeleton />) : (<Button end={true} href="." label="Overview" variant="panel"/>)}
        <Divider />
        {!community || loading ? (<Skeleton />) : (<Button href="plans" label="Plans" variant="panel"/>)}
        <Divider />
        <Typography variant="sectionHeader" marginTop="8px">Actions</Typography>
        <Divider />
        <PermissionFilter all={[ACLS.ADD_PLANNING_AREA, ACLS.ADD_COMMUNITY]}>
          {
          /* This link will not open in the Routes outlet area because the matching route is defined in AppRoutes */
            !community ? (<Skeleton />) : (<Button href="addPlan" label="Add Plan" variant="panel"/>)
          }
        </PermissionFilter>
        <PermissionFilter permission={ACLS.DELETE_COMMUNITY}>
          {deleteArea()}
        </PermissionFilter>
      </>
    );
  }

  if (!loading && (!community || communityState?.error)) {
    return (
      <Container>
        <Card>
          <CardContent>
            <Typography sx={{textAlign: 'center'}} variant="panelHeader">{communityState?.error || 'There was a problem with this community'}</Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={2.5}>
          <Container maxWidth={false} sx={{height: '100%'}} disableGutters>
            <Card sx={{height: '100%'}}>
              <CardContent>
                {renderPanel()}
              </CardContent>
            </Card>
          </Container>
        </Grid>
        <Grid item xs={9.5}>
          <Routes>
            {/* index is needed to correctly use overview as the root of this route AND correctly apply active to panel links */}
            <Route index element={<CommunityOverview community={community} loading={loading} />} />
            <Route path="plans" element={
              <PermissionFilter permission={ACLS.VIEW_PLANS}>
                {/* Remove this when we finish the story*/}
                <CommunityPlans id={id} />
              </PermissionFilter>
            } />
          </Routes>
        </Grid>
      </Grid>
      <LoadingSpinner isActive={modifying} />
    </>
  );
};

export default ViewCommunity;
